import { ref } from 'vue';

/**
 * First-run guided tour for LiveRadarPage: 6 fixed steps covering the concept, location,
 * monitoring, map, event feed, and privacy. Steps are static content (no i18n/config), so they
 * live as a plain constant rather than being passed in.
 */

export const ONBOARDING_STORAGE_KEY = 'pushka:onboardingSeenV1';

export interface OnboardingStep {
  id: string;
  title: string;
  body: string;
  /** Matches a `data-onboarding-target` attribute elsewhere in the page; null = no spotlight. */
  target: string | null;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'concept',
    title: 'Как это работает',
    body:
      'Ваш телефон слушает окружение и сравнивает с другими телефонами рядом. Когда необычный ' +
      'звук слышат сразу несколько сенсоров, приложение по разнице во времени определяет, ' +
      'откуда он донёсся — и показывает это на карте.',
    target: null,
  },
  {
    id: 'location',
    title: 'Ваше местоположение',
    body:
      'Обычно местоположение определяется автоматически — через GPS или Telegram. Если ' +
      'делиться точным положением небезопасно, в настройках (⚙) можно указать его вручную, ' +
      'с сознательной неточностью — этого достаточно для работы радара, а точное место ' +
      'останется только у вас.',
    target: 'tools-btn',
  },
  {
    id: 'mic',
    title: 'Кнопка мониторинга',
    body:
      'Нажмите на эту кнопку, чтобы включить прослушивание микрофона. Телефон начнёт ' +
      'анализировать звук локально — как только заметит что-то необычное, это попадёт в общую ' +
      'картину.',
    target: 'mic-chip',
  },
  {
    id: 'map',
    title: 'Карта событий',
    body:
      'На карте появляются события в радиусе ~20 км вокруг вас — как ваши собственные, так и ' +
      'подтверждённые другими участниками рядом.',
    target: 'map-area',
  },
  {
    id: 'feed',
    title: 'Лента событий',
    body:
      'Здесь два списка: «🌐 Рядом» — события от других участников поблизости, и «🎤 Мои ' +
      'события» — то, что обнаружил лично ваш телефон, со статусом отправки.',
    target: 'feed-tabs',
  },
  {
    id: 'privacy',
    title: 'Приватность',
    body:
      'Запись звука никогда не покидает ваш телефон. На сервер уходит только короткий ' +
      'цифровой отпечаток — несколько чисел, — по которому нельзя восстановить, что именно вы ' +
      'слышали.',
    target: null,
  },
];

function loadSeen(): boolean {
  try {
    // Automation / manual escape hatch: `?onboarding=off` in the URL suppresses the first-run
    // tour so e2e harnesses (tdoa/flyby multisensor) aren't blocked by the click-intercepting
    // overlay, and so the radar can be demoed without it. Does NOT persist the seen flag.
    if (typeof location !== 'undefined' && /[?&]onboarding=off(?:&|$)/.test(location.search)) {
      return true;
    }
    return localStorage.getItem(ONBOARDING_STORAGE_KEY) === '1';
  } catch {
    // Storage unavailable (private mode / disabled): don't force the tour on every reload.
    return true;
  }
}

function saveSeen(): void {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function useOnboarding() {
  const stepIndex = ref(0);
  const visible = ref(!loadSeen());
  const totalSteps = ONBOARDING_STEPS.length;

  function finish(): void {
    visible.value = false;
    saveSeen();
  }

  function next(): void {
    if (stepIndex.value >= totalSteps - 1) {
      finish();
      return;
    }
    stepIndex.value += 1;
  }

  function back(): void {
    if (stepIndex.value === 0) return;
    stepIndex.value -= 1;
  }

  function skip(): void {
    finish();
  }

  function restart(): void {
    stepIndex.value = 0;
    visible.value = true;
  }

  return {
    steps: ONBOARDING_STEPS,
    stepIndex,
    visible,
    totalSteps,
    next,
    back,
    skip,
    finish,
    restart,
  };
}
